import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Minus, Plus, ShoppingBag, Check, EyeOff, Heart } from 'lucide-react';
import { PromoBadge, PromoInfo } from '../components/PromoBadge';
import axios from 'axios';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useFavorites } from '../context/FavoritesContext';
import { toast } from 'sonner';
import PricingTiers from '../components/PricingTiers';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ProductDetailPage = () => {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [added, setAdded] = useState(false);
  const { addToCart, getPreviewPrice } = useCart();
  const { isAuthenticated, user } = useAuth();
  // Same B2B gate as ProductCard — wholesale tiers reserved to approved customers.
  const canSeeWholesale = isAuthenticated && user?.status === 'approved';
  const { t } = useLanguage();
  const { isFavorite, toggleFavorite } = useFavorites();

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/products/${id}`);
        setProduct(response.data);
        // Track view (fire and forget)
        axios.post(`${API_URL}/api/products/${id}/view`).catch(() => {});
      } catch (error) {
        // Error shown via loading=false + null data check in UI
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [id]);

  const handleAddToCart = () => {
    if (product && quantity > 0) {
      addToCart(product, quantity);
      setAdded(true);
      setTimeout(() => setAdded(false), 2000);
    }
  };

  const getCurrentPrice = () => {
    if (!product) return 0;
    if (!canSeeWholesale) return product.price_detail;
    return getPreviewPrice(product, quantity).unit;
  };

  const currentTierName = (() => {
    if (!product || !canSeeWholesale) return 'detail';
    return getPreviewPrice(product, quantity).tier;
  })();

  if (loading) {
    return (
      <div className="min-h-screen bg-main pt-24 pb-16 flex items-center justify-center">
        <div className="animate-pulse text-[color:var(--color-primary)]">{t('loading')}</div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-main pt-24 pb-16 flex flex-col items-center justify-center">
        <p className="text-muted mb-4">{t('product_not_found')}</p>
        <Link to="/catalogue" className="btn-outline">
          {t('product_back_catalogue')}
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-main pt-24 pb-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link 
          to="/catalogue" 
          className="inline-flex items-center gap-2 text-muted hover:text-heading transition-colors mb-8"
          data-testid="back-to-catalogue"
        >
          <ArrowLeft className="w-4 h-4" />
          {t('product_back_catalogue')}
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Product Image */}
          <div className="aspect-[3/4] card-surface overflow-hidden">
            <img
              src={product.image_url}
              alt={product.name}
              className="w-full h-full object-cover"
              onError={(e) => {
                e.target.src = 'https://images.unsplash.com/photo-1541643600914-78b084683601?w=800';
              }}
            />
          </div>

          {/* Product Info */}
          <div className="flex flex-col">
            <div className="flex items-center gap-4 mb-4">
              <span className="text-[color:var(--color-primary)] text-sm uppercase tracking-widest">{product.brand}</span>
              <span className="text-muted">•</span>
              <span className="text-muted text-sm uppercase tracking-widest">{product.category}</span>
              {product.promo && <PromoBadge product={product} />}
            </div>

            <h1 
              className="text-4xl sm:text-5xl text-heading font-light mb-6"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
              data-testid="product-name"
            >
              {product.name}
            </h1>

            {/* Favorite button */}
            <button
              type="button"
              onClick={() => {
                if (!isAuthenticated) {
                  toast.error(t('product_login_for_favorites'));
                  return;
                }
                toggleFavorite(product.id);
              }}
              className={`flex items-center gap-2 mb-6 px-4 py-2 border transition-colors ${
                isFavorite(product.id)
                  ? 'border-red-500/30 bg-red-500/10 text-red-500'
                  : 'border-white/10 text-muted hover:text-red-500 hover:border-red-500/30'
              }`}
              data-testid="product-detail-favorite"
            >
              <Heart className={`w-4 h-4 ${isFavorite(product.id) ? 'fill-current' : ''}`} />
              {isFavorite(product.id) ? t('favorites_saved') : t('nav_favorites')}
            </button>

            <p className="text-body leading-relaxed mb-8" data-testid="product-description">
              {product.description}
            </p>

            {/* Stock Status */}
            <div className="mb-6">
              {product.stock > 0 ? (
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-[#4ADE80] rounded-full" />
                  <span className="text-[#4ADE80] text-sm">
                    {t('product_in_stock', { count: product.stock })}
                  </span>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-red-500 rounded-full" />
                  <span className="text-red-500 text-sm">{t('product_out_of_stock_full')}</span>
                </div>
              )}
              {product.preorder_enabled && (
                <Link to="/precommande" className="mt-2 inline-flex items-center gap-2 text-[color:var(--color-primary)] text-sm underline underline-offset-4"
                  data-testid="product-preorder-link">
                  {t('product_preorder_link')}
                  {product.preorder_expected_date && ` (${new Date(product.preorder_expected_date).toLocaleDateString('fr-FR')})`}
                </Link>
              )}
            </div>

            {/* Pricing */}
            <div className="mb-8">
              {isAuthenticated && canSeeWholesale ? (
                <div>
                  <PromoInfo product={product} showOld={false} className="mb-3" />
                  <PricingTiers 
                    product={product} 
                    quantity={quantity} 
                    setQuantity={setQuantity} 
                  />
                </div>
              ) : (
                <div>
                  <div className="flex items-baseline gap-3 mb-4">
                    <span className="text-muted text-xs uppercase tracking-wider">{t('product_retail_price')}</span>
                    <span className={`text-3xl font-light ${product.promo ? 'text-red-400' : 'text-[color:var(--color-primary)]'}`} style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                      {product.price_detail}€
                    </span>
                    {product.price_detail_original && <span className="text-muted line-through text-lg" data-testid={`promo-old-price-${product.id}`}>{product.price_detail_original}€</span>}
                  </div>
                  <PromoInfo product={product} showOld={false} className="mb-4" />
                  <div className="flex items-center gap-3 p-4 bg-[color:rgb(var(--primary-rgb)/0.05)] border border-[color:rgb(var(--primary-rgb)/0.20)]" data-testid="price-locked-detail">
                    <EyeOff className="w-5 h-5 text-[color:var(--color-primary)] flex-shrink-0" />
                    <div>
                      <p className="text-heading text-sm">{t('product_pro_prices')}</p>
                      <Link to="/inscription" className="text-[color:var(--color-primary)] text-sm hover:underline">
                        {t('product_register_pro')}
                      </Link>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Quantity Selector */}
            <div className="mb-8">
              <label className="input-label">{t('product_quantity')}</label>
              <div className="flex items-center gap-4">
                <button
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  className="p-3 card-surface hover:border-[color:rgb(var(--primary-rgb)/0.30)] transition-colors"
                  disabled={quantity <= 1}
                  data-testid="decrease-quantity"
                >
                  <Minus className="w-5 h-5 text-body" />
                </button>
                <input
                  type="number"
                  min="1"
                  max={product.stock}
                  value={quantity}
                  onChange={(e) => setQuantity(Math.max(1, Math.min(product.stock, parseInt(e.target.value) || 1)))}
                  className="w-20 input-field text-center"
                  data-testid="quantity-input"
                />
                <button
                  onClick={() => setQuantity(Math.min(product.stock, quantity + 1))}
                  className="p-3 card-surface hover:border-[color:rgb(var(--primary-rgb)/0.30)] transition-colors"
                  disabled={quantity >= product.stock}
                  data-testid="increase-quantity"
                >
                  <Plus className="w-5 h-5 text-body" />
                </button>
              </div>
            </div>

            {/* Total */}
            <div className="card-surface p-6 mb-8">
              <div className="flex justify-between items-center">
                <span className="text-muted">{t('cart_total')}</span>
                <span 
                  className="text-3xl text-[color:var(--color-primary)] font-light"
                  style={{ fontFamily: 'Cormorant Garamond, serif' }}
                  data-testid="total-price"
                >
                  {(getCurrentPrice() * quantity).toFixed(2)}€
                </span>
              </div>
            </div>

            {/* Add to Cart */}
            <button
              onClick={handleAddToCart}
              disabled={product.stock <= 0 || added}
              className={`w-full flex items-center justify-center gap-3 py-4 px-8 transition-all duration-300 ${
                added 
                  ? 'bg-[#4ADE80] text-black rounded-xl' 
                  : 'btn-primary disabled:opacity-50'
              } disabled:cursor-not-allowed`}
              data-testid="add-to-cart-button"
            >
              {added ? (
                <>
                  <Check className="w-5 h-5" />
                  {t('product_added')}
                </>
              ) : (
                <>
                  <ShoppingBag className="w-5 h-5" />
                  {t('product_add_to_cart')}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetailPage;
